import { useCallback, useEffect, useId, useRef, useState } from "react";
import { ROLE_LABELS } from "@velivo/shared";
import { useAuth } from "../auth/AuthContext";
import { Icon } from "./Icon";

/**
 * FR-ADMIN-04 / DESIGN_SYSTEM.md §4 — a persistent Brand switcher in the
 * primary nav for anyone with access to more than one Brand.
 *
 * The skill note is explicit that this is structural, not cosmetic: "building a
 * screen that silently assumes 'the user's one Brand' will break for every
 * OrganizationAdmin and every agency user on day one."
 *
 * Because it announces itself as a listbox, it has to behave like one — the
 * ARIA APG pattern (arrow keys, Home/End, focus returning to the trigger on
 * close). A role that promises keyboard semantics it doesn't implement is worse
 * for a screen-reader user than a plain button would have been (WCAG 4.1.2).
 */
export function BrandSwitcher() {
  const { user, activeBrand, setActiveBrand } = useAuth();
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();

  const grants = user?.brandGrants ?? [];

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    // Tabbing out of the last option must close the menu too — otherwise it
    // stays visibly open, overlapping the page, while focus has moved on.
    function onFocusIn(event: FocusEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [open]);

  useEffect(() => {
    if (open) optionRefs.current[focusedIndex]?.focus();
  }, [open, focusedIndex]);

  if (grants.length === 0) return null;

  // With exactly one Brand there's nothing to switch between — showing a
  // dropdown would be a control that never does anything.
  if (grants.length === 1) {
    return (
      <div className="brandswitcher">
        <div className="brandswitcher__trigger" style={{ cursor: "default" }}>
          <span style={{ minWidth: 0 }}>
            <span className="brandswitcher__label">Brand</span>
            <span className="brandswitcher__name">{grants[0]!.brandName}</span>
          </span>
        </div>
      </div>
    );
  }

  const activeIndex = Math.max(
    0,
    grants.findIndex((g) => g.brandId === activeBrand?.brandId),
  );

  function openMenu(startIndex: number) {
    setFocusedIndex(startIndex);
    setOpen(true);
  }

  function select(brandId: string) {
    setActiveBrand(brandId);
    close(true);
  }

  function onMenuKeyDown(event: React.KeyboardEvent) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setFocusedIndex((i) => (i + 1) % grants.length);
        break;
      case "ArrowUp":
        event.preventDefault();
        setFocusedIndex((i) => (i - 1 + grants.length) % grants.length);
        break;
      case "Home":
        event.preventDefault();
        setFocusedIndex(0);
        break;
      case "End":
        event.preventDefault();
        setFocusedIndex(grants.length - 1);
        break;
      case "Escape":
        event.preventDefault();
        close(true);
        break;
      default:
        break;
    }
  }

  return (
    <div className="brandswitcher" ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className="brandswitcher__trigger"
        onClick={() => (open ? close(false) : openMenu(activeIndex))}
        onKeyDown={(event) => {
          if (
            event.key === "ArrowDown" ||
            event.key === "Enter" ||
            event.key === " "
          ) {
            event.preventDefault();
            openMenu(activeIndex);
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
      >
        <span style={{ minWidth: 0 }}>
          <span className="brandswitcher__label">Brand</span>
          <span className="brandswitcher__name">
            {activeBrand?.brandName ?? "Select a brand"}
          </span>
        </span>
        <span style={{ color: "var(--color-text-tertiary)", display: "flex" }}>
          <Icon name="chevronDown" size={16} />
        </span>
      </button>

      {open && (
        <div
          className="brandswitcher__menu"
          id={menuId}
          role="listbox"
          aria-label="Switch brand"
          onKeyDown={onMenuKeyDown}
        >
          {grants.map((grant, index) => (
            <button
              type="button"
              key={grant.brandId}
              ref={(el) => {
                optionRefs.current[index] = el;
              }}
              className="brandswitcher__option"
              role="option"
              aria-selected={grant.brandId === activeBrand?.brandId}
              tabIndex={index === focusedIndex ? 0 : -1}
              onClick={() => select(grant.brandId)}
            >
              <span>{grant.brandName}</span>
              <span className="brandswitcher__role">
                {ROLE_LABELS[grant.role]}
                {grant.inherited ? " · via Organization" : ""}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
